<script setup>
import { computed, nextTick, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import LaisCard from 'dashboard/components-next/LaisCard.vue';
import LaisPrimaryButton from 'dashboard/components-next/LaisPrimaryButton.vue';
import SalesPartyRegistrationNav from './SalesPartyRegistrationNav.vue';
import SalesPartyRegistrationProgressFooter from './SalesPartyRegistrationProgressFooter.vue';

const props = defineProps({
  activeSection: { type: String, required: true },
  canEdit: { type: Boolean, default: false },
  currentBindings: { type: Object, default: () => ({}) },
  currentComponent: { type: [Object, Function], default: null },
  currentIndex: { type: Number, required: true },
  hasParty: { type: Boolean, default: false },
  isComplete: { type: Function, required: true },
  loading: { type: Boolean, default: false },
  partyKind: { type: String, default: '' },
  progressPercent: { type: Number, default: null },
  reviewCount: { type: Number, default: 0 },
  reviewMode: { type: Boolean, default: false },
  saving: { type: Boolean, default: false },
  sectionChecks: { type: Object, default: () => ({}) },
  sections: { type: Array, default: () => [] },
});

const emit = defineEmits([
  'changed',
  'completeReview',
  'next',
  'previous',
  'save',
  'select',
  'showCorrections',
  'update',
]);
const { t } = useI18n();
const forwardUpdate = (...args) => emit('update', ...args);
const isLastSection = computed(
  () => props.currentIndex === props.sections.length - 1
);
const nextActionLabel = computed(() => {
  if (isLastSection.value) {
    return t('SALES_PARTIES.ACTIONS.COMPLETE_REGISTRATION');
  }

  return t('SALES_PARTIES.ACTIONS.NEXT');
});
const contentViewport = ref(null);
const resetRegistrationScroll = async () => {
  await nextTick();
  contentViewport.value?.scrollTo?.({ top: 0, behavior: 'auto' });
  if (typeof document === 'undefined') return;

  document.documentElement.scrollTop = 0;
  document.body.scrollTop = 0;
  document.scrollingElement?.scrollTo?.({ top: 0, behavior: 'auto' });
};
watch(() => props.activeSection, resetRegistrationScroll);
</script>

<template>
  <main
    data-test="sales-party-registration-content"
    class="flex min-h-[calc(100vh-66px)] w-full bg-[#F5F7F8]"
  >
    <SalesPartyRegistrationNav
      :active-section="activeSection"
      :can-edit="canEdit"
      :party-kind="partyKind"
      :sections="sections"
      :is-complete="isComplete"
      :review-mode="reviewMode"
      :review-count="reviewCount"
      :saving="saving"
      :section-checks="sectionChecks"
      @save="emit('save')"
      @select="emit('select', $event)"
    />

    <section
      ref="contentViewport"
      class="min-w-0 flex-1 overflow-y-auto"
      :class="
        reviewMode
          ? 'pb-[100px] min-[701px]:h-[calc(100vh-66px)] min-[701px]:pb-[116px]'
          : 'pb-6 min-[701px]:h-[calc(100vh-66px)] min-[701px]:pb-[82px]'
      "
    >
      <div
        class="w-full min-[701px]:ml-[50px] min-[701px]:w-[70vw]"
        :class="reviewMode ? 'pt-[40px]' : 'pt-[50px]'"
      >
        <div
          data-test="sales-party-registration-card"
          class="rounded-[10px] bg-white p-8 font-roboto shadow-[0_2px_4px_rgba(0,0,0,0.08)]"
        >
          <div
            v-if="loading"
            class="py-8 text-center text-sm text-lais-text-muted"
          >
            {{ t('SALES_PARTIES.COMMON.LOADING') }}
          </div>
          <LaisCard
            v-else-if="activeSection === 'documents' && !hasParty"
            :title="t('SALES_PARTIES.SECTIONS.DOCUMENTS')"
            prefix-icon="i-lucide-files"
          >
            <p class="text-sm text-lais-text-muted">
              {{ t('SALES_PARTIES.REGISTRATION.WAIT_PARTY') }}
            </p>
          </LaisCard>
          <component
            :is="currentComponent"
            v-else
            v-bind="currentBindings"
            class="!rounded-none !bg-transparent !p-0 !shadow-none"
            @update="forwardUpdate"
            @changed="emit('changed')"
          />

          <div
            v-if="!reviewMode"
            class="mt-8 flex flex-wrap items-center justify-between gap-3 max-[700px]:flex-nowrap max-[700px]:gap-2"
          >
            <LaisPrimaryButton
              v-if="currentIndex > 0"
              variant="ghost"
              icon="i-lucide-arrow-left"
              class="!rounded-[10px] !border-0 !px-[25px] !py-[15px] !font-roboto !text-base !font-bold !text-[#009F94]"
              @click="emit('previous')"
            >
              {{ t('SALES_PARTIES.ACTIONS.PREVIOUS') }}
            </LaisPrimaryButton>
            <span v-else />
            <LaisPrimaryButton
              :is-loading="saving"
              :disabled="!canEdit"
              class="!min-h-[58px] !w-auto !min-w-[139px] !rounded-[10px] !bg-[#14B8AA] !px-[25px] !py-[15px] !font-roboto !text-base !font-bold hover:!bg-[#01655D]"
              @click="emit('next')"
            >
              <span>{{ nextActionLabel }}</span>
              <span
                class="i-lucide-arrow-right ml-2 size-5"
                aria-hidden="true"
              />
            </LaisPrimaryButton>
          </div>
        </div>
      </div>
    </section>

    <footer
      v-if="reviewMode"
      data-test="sales-party-review-footer"
      class="fixed bottom-0 left-0 right-0 z-20 flex min-h-[82px] items-center justify-between gap-3 border-t border-[#DCE3E6] bg-white px-6 py-5 font-roboto shadow-[0_2px_4px_rgba(0,0,0,0.08)] min-[701px]:left-[295px]"
    >
      <LaisPrimaryButton
        v-if="reviewCount"
        variant="ghost"
        class="!border-0 !px-0 !font-roboto !text-base !font-bold !text-[#009F94]"
        @click="emit('showCorrections')"
      >
        {{ t('SALES_PARTIES.REVIEW.VIEW', { count: reviewCount }) }}
      </LaisPrimaryButton>
      <span v-else />
      <LaisPrimaryButton
        icon="i-lucide-check"
        :is-loading="saving"
        :disabled="!canEdit"
        class="!min-h-[58px] !min-w-[191px] !rounded-[10px] !bg-[#14B8AA] !px-6 !font-roboto !text-base !font-bold hover:!bg-[#01655D]"
        @click="emit('completeReview')"
      >
        {{ t('SALES_PARTIES.REVIEW.COMPLETE') }}
      </LaisPrimaryButton>
    </footer>

    <SalesPartyRegistrationProgressFooter
      v-else
      :can-edit="canEdit"
      :is-complete="isComplete"
      :party-kind="partyKind"
      :progress-percent="progressPercent"
      :saving="saving"
      :sections="sections"
      @save="emit('save')"
    />
  </main>
</template>
